import {
  Alert,
  AlertIcon,
  AspectRatio,
  Box,
  Button,
  Flex,
  Image,
  GridProps,
  Stack,
  Text,
  useDisclosure,
  Heading,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ColouredTickList } from "../components/ColouredTickList";
import { ContentWidthContainer } from "../components/ContentWidthContainer";
import { ImageCarousel } from "../components/ImageCarousel";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { PageHeader } from "../components/PageHeader";
import { CaravanFormModal } from "../forms/CaravanFormModal";
import { ParkFormModal } from "../forms/ParkFormModal";
import { useGetCaravanDetails } from "../hooks/useGetCaravanDetails";
import { useGetParkDetails } from "../hooks/useGetParkDetails";
import { Image as ImageType } from "../types";
import { capitalizeWordsNoUnderscore, formatNumberWithPounds } from "../utils";
import { decodeHtmlEntities } from "../utils/decodeHtmlEntities";
import { Helmet } from "react-helmet";

export const CaravanPage: React.FC<GridProps> = (props) => {
  const { park } = useParams();
  const {
    data: caravanDetails,
    isPending: caravanDetailsPending,
    isError: caravanDetailsError,
  } = useGetCaravanDetails();
  const { data: parkDetails, isPending: parkDetailsPending, isError: parkDetailsError } = useGetParkDetails();

  const { isOpen: isParkFormOpen, onOpen: onParkFormOpen, onClose: onParkFormClose } = useDisclosure();
  const { isOpen: isCaravanFormOpen, onOpen: onCaravanFormOpen, onClose: onCaravanFormClose } = useDisclosure();

  const parkFeaturesSummary = useMemo(() => parkDetails?.facilities || [], [parkDetails]);

  if (caravanDetailsPending || parkDetailsPending) {
    return <LoadingSpinner />;
  }

  if (caravanDetailsError || parkDetailsError || !caravanDetails || !parkDetails) {
    return <CaravanError park={park as string} />;
  }

  const { images, model, manufacturer, bedrooms, ID, yearBuilt, longDesc, amenities, price } = caravanDetails;

  const imagesURLs = images?.map((image: ImageType) => image.url) || [];
  const imagesURLsSizeCapped = imagesURLs.map((img) => `${img}&width=800`);

  return (
    <>
      <Helmet>
        <title>
          {manufacturer}, {model}
        </title>
        <meta name="description" content={longDesc} />
      </Helmet>
      <ParkFormModal isOpen={isParkFormOpen} onClose={onParkFormClose} parkId={parkDetails.parkId} />
      <CaravanFormModal isOpen={isCaravanFormOpen} onClose={onCaravanFormClose} caravanId={ID} />

      <PageHeader justify="space-between">
        <>
          <Heading as="h1">
            {manufacturer}, {model}
          </Heading>
          <Text>{formatNumberWithPounds(price)}</Text>
        </>
      </PageHeader>

      <ContentWidthContainer>
        <Flex w="100%" gap={4} flexDir={{ base: "column", lg: "row" }} {...props}>
          <Stack w={{ base: "100%", lg: "50%" }}>
            <Flex flexDir="column" fontWeight="600" gap={2} mb={2}>
              <Text>Ad Ref: {ID}</Text>
              <Text>
                Park:{" "}
                <Link to=".." relative="path" style={{ textDecoration: "underline" }}>
                  {capitalizeWordsNoUnderscore(park as string)}
                </Link>
              </Text>

              <Text>Bedrooms: {bedrooms}</Text>
              <Text>Year: {yearBuilt}</Text>
            </Flex>

            <Text fontSize="19px" fontWeight="700" mb={2}>
              Caravan Features Summary
            </Text>
            <ColouredTickList data={amenities} columns={{ base: 1, lg: 2 }} />

            <Box borderWidth={1} mt={4}>
              <Flex bg="white" justify="center" p={2}>
                <Button onClick={onParkFormOpen} p={2} borderRadius="20px" w="300px">
                  <Text>Request a Free Park Pack</Text>
                </Button>
              </Flex>
              <Flex bg="gray.50" justify="center" p={2}>
                <Button
                  onClick={onCaravanFormOpen}
                  w="300px"
                  variant="blue"
                  textTransform="uppercase"
                  color="white"
                  p={2}
                >
                  Contact Seller
                </Button>
              </Flex>
            </Box>
          </Stack>

          <Stack w={{ base: "100%", lg: "50%" }}>
            {imagesURLsSizeCapped.length > 0 && (
              <ImageCarousel w={{ base: "100%", md: "70%", lg: "100%" }} mx="auto">
                {imagesURLsSizeCapped.map((url, i) => (
                  <AspectRatio key={i} ratio={4 / 3} w="100%">
                    <Image src={url} h="100%" w="100%" objectFit="cover" />
                  </AspectRatio>
                ))}
              </ImageCarousel>
            )}

            <Box
              dangerouslySetInnerHTML={{
                __html: decodeHtmlEntities(longDesc as string),
              }}
            />

            {parkFeaturesSummary?.length > 0 && (
              <>
                <Text fontSize="22px" fontWeight="700">
                  Park Features Summary
                </Text>
                <ColouredTickList data={parkFeaturesSummary} columns={{ base: 1, lg: 2 }} />
              </>
            )}
          </Stack>
        </Flex>
      </ContentWidthContainer>
    </>
  );
};

const CaravanError: React.FC<{ park: string }> = ({ park }) => (
  <Box p={4}>
    <Alert status="warning">
      <Flex flexDir="row">
        <AlertIcon />
        <Flex flexDir="column">
          Sorry, we could not find the relevant Caravan data
          <Box maxWidth="sm">
            <Link to=".." relative="path" style={{ textDecoration: "underline" }}>
              Back to {capitalizeWordsNoUnderscore(park) || "Park"}
            </Link>
          </Box>
        </Flex>
      </Flex>
    </Alert>
  </Box>
);
